import { Search, Globe, Megaphone, Palette, BarChart3, Mail, MapPin, Share2, Video, PenTool, Sparkles, Smartphone, Code, ShoppingCart, Target, Camera, Briefcase, Layers } from "lucide-react";

// Service images - imported for webpack optimization
import localServiceAdsImg from "@/assets/services/local-service-ads.jpg";
import gmbOptimizationImg from "@/assets/services/gmb-optimization.jpg";
import seoImg from "@/assets/services/seo.jpg";
import webDesignImg from "@/assets/services/web-design.jpg";
import ppcImg from "@/assets/services/ppc.jpg";
import socialMediaImg from "@/assets/services/social-media.jpg";
import analyticsImg from "@/assets/services/analytics.jpg";
import emailMarketingImg from "@/assets/services/email-marketing.jpg";
import videoCreationImg from "@/assets/services/video-creation.jpg";
import graphicDesignImg from "@/assets/services/graphic-design.jpg";
import brandingImg from "@/assets/services/branding.jpg";
import socialMediaManagementImg from "@/assets/services/social-media-management.jpg";
import uiUxDesignImg from "@/assets/services/ui-ux-design.jpg";
import appDevelopmentImg from "@/assets/services/app-development.jpg";
import ecommerceImg from "@/assets/services/ecommerce.jpg";
import salesFunnelsImg from "@/assets/services/sales-funnels.jpg";
import productMarketingImg from "@/assets/services/product-marketing.jpg";
import businessConsultingImg from "@/assets/services/business-consulting.jpg";
import customBusinessSolutionsImg from "@/assets/services/custom-business-solutions.jpg";

export interface ServiceFAQ {
  question: string;
  answer: string;
}

export interface ServiceProcess {
  step: string;
  description: string;
}

// Tier 1: Revenue Drivers (Homepage + Nav Priority)
// Tier 2: Growth Accelerators (Secondary Nav + Services Page Feature)
// Tier 3: Specialized Services (Services Page + Footer)
export type ServiceTier = 1 | 2 | 3;

export type ServiceCategory = 
  | "advertising"
  | "seo"
  | "creative"
  | "business-solutions"
  | "support";

export interface Service {
  id: string;
  title: string;
  tagline: string;
  description: string;
  benefits: string[];
  icon: typeof Search;
  image: string;
  problemSolved: string;
  approach: string;
  resultMetric?: string;
  faqs?: ServiceFAQ[];
  process?: ServiceProcess[];
  relatedServices?: string[];
  tier: ServiceTier;
  category: ServiceCategory;
  isPrimary?: boolean; // Featured in homepage/nav
}
// Services ordered by: High-demand services first, with new offerings (CRM, SaaS, Backend) mixed in
// Priority order: PPC, LSA, SEO, Web Design, GMB, Social Media, App Dev, Email, Ecommerce, Analytics, Sales Funnels, etc.
export const services: Service[] = [
  // === TIER 1: REVENUE DRIVERS ===
  {
    id: "ppc",
    title: "Paid Advertising (PPC)",
    tagline: "Instant Visibility, Measurable Results",
    description: "Get in front of your ideal customers today with targeted Google and social media ads. We maximize every dollar of your ad spend.",
    benefits: [
      "Immediate visibility in search results",
      "Precise targeting to reach your ideal customer",
      "Transparent reporting on every dollar spent",
      "Continuous optimization for better ROI"
    ],
    icon: Megaphone,
    image: ppcImg,
    problemSolved: "Wasting money on ads that don't bring in real customers?",
    approach: "Strategic keyword research, compelling ad copy, landing page optimization, and relentless A/B testing.",
    tier: 1,
    category: "advertising",
    isPrimary: true,
    resultMetric: undefined,
    faqs: [
      {
        question: "How much should I spend on PPC?",
        answer: "Budget depends on your industry, competition, and goals. We recommend starting with at least $1,000-2,000/month in ad spend to gather meaningful data. We'll help you determine the right budget during our strategy call."
      },
      {
        question: "How is PPC different from SEO?",
        answer: "PPC gives you immediate visibility—you pay to appear at the top of search results. SEO builds long-term organic visibility. Most businesses benefit from both: PPC for immediate results and SEO for sustainable growth."
      },
      {
        question: "Which platforms do you advertise on?",
        answer: "We specialize in Google Ads (Search, Display, Shopping), Meta Ads (Facebook & Instagram), and LinkedIn Ads for B2B. We'll recommend the platforms where your ideal customers spend their time."
      },
      {
        question: "How do you track PPC performance?",
        answer: "We set up comprehensive conversion tracking to measure leads, calls, purchases, and other valuable actions. You'll receive regular reports showing cost per lead, return on ad spend, and other key metrics."
      }
    ],
    process: [
      { step: "Research & Strategy", description: "We research your market, competitors, and keywords to build a winning strategy." },
      { step: "Campaign Setup", description: "We create campaigns with precise targeting, compelling ads, and proper tracking." },
      { step: "Landing Page Optimization", description: "We ensure your landing pages are optimized to convert ad traffic into leads." },
      { step: "Launch & Monitor", description: "We launch campaigns and closely monitor performance in the first weeks." },
      { step: "Optimize & Scale", description: "We continuously test and optimize to improve results and scale what works." }
    ],
    relatedServices: ["local-service-ads", "analytics", "web-design"]
  },
  {
    id: "local-service-ads",
    title: "Local Service Ads",
    tagline: "Get Leads That Call You First",
    description: "Appear at the very top of Google search results with Google's Local Service Ads. Pay only for real leads, not clicks.",
    benefits: [
      "Top placement above all other ads",
      "Pay per lead, not per click",
      "Google Guaranteed badge builds trust",
      "Direct calls and messages from ready buyers"
    ],
    icon: MapPin,
    image: localServiceAdsImg,
    problemSolved: "Tired of paying for clicks that never convert to customers?",
    approach: "We set up, verify, and optimize your LSA profile to maximize lead quality and minimize cost per acquisition.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What are Local Service Ads?",
        answer: "Local Service Ads (LSAs) are Google ads that appear at the very top of search results for local service queries. Unlike traditional PPC, you only pay when a customer contacts you directly through the ad—not for clicks."
      },
      {
        question: "How is LSA different from Google Ads?",
        answer: "LSAs charge per lead (call, message, or booking) rather than per click. They also appear above regular Google Ads and include a Google Guaranteed badge that builds trust with potential customers."
      },
      {
        question: "What businesses qualify for Local Service Ads?",
        answer: "LSAs are available for home service providers, legal services, real estate, healthcare providers, and many other local service categories. We'll help determine if your business qualifies during our consultation."
      },
      {
        question: "How long until I start getting leads?",
        answer: "Once your profile is verified and approved (typically 2-4 weeks), you can start receiving leads immediately. Most clients see their first quality leads within the first week of going live."
      }
    ],
    process: [
      { step: "Profile Setup", description: "We create and optimize your LSA profile with compelling descriptions and accurate service areas." },
      { step: "Verification", description: "We guide you through Google's background check and license verification process." },
      { step: "Budget Configuration", description: "We set optimal weekly budgets and bid strategies for your target leads." },
      { step: "Launch & Monitor", description: "Your ads go live and we monitor performance, adjusting to maximize lead quality." },
      { step: "Optimization", description: "We continuously refine targeting and respond strategies to lower cost per lead." }
    ],
    relatedServices: ["gmb-optimization", "ppc", "seo"],
    tier: 1,
    category: "advertising",
    isPrimary: true
  },
  // === TIER 1: SEO ===
  {
    id: "seo",
    title: "Search Engine Optimization",
    tagline: "Get Found by Customers Searching for You",
    description: "Dominate search results and drive qualified traffic to your business. We use proven strategies that help local businesses outrank their competition.",
    benefits: [
      "Higher Google rankings for your key services",
      "More qualified leads finding your business",
      "Long-term organic traffic growth",
      "Local search dominance in Michigan"
    ],
    icon: Search,
    image: seoImg,
    problemSolved: "Tired of being invisible online while competitors steal your customers?",
    approach: "We audit your site, optimize for your target keywords, build quality backlinks, and ensure Google loves your content.",
    resultMetric: undefined,
    faqs: [
      {
        question: "How long does SEO take to show results?",
        answer: "SEO is a long-term strategy. You'll typically see initial improvements in 3-4 months, with significant results in 6-12 months. The good news is that these results compound over time, unlike paid ads which stop when you stop paying."
      },
      {
        question: "What's the difference between local and national SEO?",
        answer: "Local SEO focuses on ranking for location-based searches (like 'plumber near me') and optimizing your Google Business Profile. National SEO targets broader keywords without geographic modifiers. Most local businesses benefit most from local SEO."
      },
      {
        question: "Do you guarantee first page rankings?",
        answer: "We don't guarantee specific rankings because Google's algorithm is constantly changing. However, we do guarantee our proven process and transparent reporting. Our track record shows consistent improvements for our clients."
      },
      {
        question: "How do you measure SEO success?",
        answer: "We track rankings for target keywords, organic traffic growth, leads/conversions from organic search, and your visibility in local search results. You'll receive monthly reports showing progress on all key metrics."
      }
    ],
    process: [
      { step: "SEO Audit", description: "Comprehensive technical, on-page, and competitive analysis of your current position." },
      { step: "Keyword Strategy", description: "We identify the highest-value keywords your customers are actually searching for." },
      { step: "On-Page Optimization", description: "We optimize your website's content, meta tags, structure, and technical elements." },
      { step: "Off-Page Building", description: "Quality backlink building and local citations to boost your domain authority." },
      { step: "Ongoing Optimization", description: "Monthly content updates, link building, and strategy refinement based on results." }
    ],
    relatedServices: ["web-design", "gmb-optimization", "analytics"],
    tier: 1,
    category: "seo",
    isPrimary: true
  },
  // === TIER 1: WEB DESIGN ===
  {
    id: "web-design",
    title: "Website Design & Custom Web Platforms",
    tagline: "High-Performance Owned Digital Platforms That Generate Inbound Leads 24/7",
    description: "Your website is not an online brochure—it is your business's core digital operating asset. We engineer ultra-fast, mobile-first websites and custom interactive platforms (from instant quote calculators and 2D/3D building configurators to tenant locators and audio engines) designed to command regional authority, dominate local Google rankings, and convert high-ticket visitors into booked revenue.",
    benefits: [
      "Custom Interactive Calculators & Schedulers (Estimators, 2D/3D Configurators, Unit Locators)",
      "Sub-Second Speed-to-Lead Automation (SMS Alerts & CRM Pipeline Sync within 60 Seconds)",
      "Built-in Local SEO & Multi-County Silos (Dominate Local Search & Service Area Visibility)",
      "Zero-Bloat Next-Gen Performance (Sub-Second Load Times & 95+ Core Web Vitals)",
      "Mobile-First Conversion Ergonomics (Instant Tap-to-Call & Sticky Lead Capture Flows)",
      "100% Owned Digital Independence (Own Your Entire Codebase With Zero Recurring SaaS Rent)"
    ],
    icon: Globe,
    image: webDesignImg,
    problemSolved: "Most business websites are built on bloated, generic rental templates (Wix, Squarespace, or fragile plugin-heavy WordPress) that take 6+ seconds to load, fail on mobile phones, and leak 70%+ of their visitors. When potential customers can't find instant pricing, service boundaries, or direct scheduling, they bounce straight to your competitor in under 5 seconds.",
    approach: "We replace static brochures with owned conversion architectures. Every Valentine Digital platform incorporates custom interactive tools tailored to your exact industry—such as interactive dimension selectors, instant square-footage estimators, automated speed-to-lead routing, and county-specific SEO silos. You own 100% of your code and data with zero recurring SaaS rental lock-in.",
    resultMetric: "350%+ Average Lift in Inbound Quote Inquiries",
    faqs: [
      {
        question: "How are Valentine Digital websites different from generic templates or DIY builders?",
        answer: "Most agencies sell recycled WordPress or Squarespace templates filled with 30+ unmaintained plugins that slow your site down and require endless monthly subscription fees. We build custom-engineered, lightweight web platforms tailored specifically to your trade or service. We build real interactive tools into your site—such as custom quote calculators, 2D/3D configurators, automated vacancy locators, and sub-second lead routing engines—so your site actually does real work for your sales team."
      },
      {
        question: "Do I truly own the website once it's launched?",
        answer: "100% yes. Unlike proprietary website builders or agencies that lock you into proprietary hosting contracts where you lose your site if you leave, you own your codebase, database, design assets, and domain. There is zero vendor lock-in and zero mandatory recurring software rent."
      },
      {
        question: "Can you build custom interactive tools like calculators, configurators, or booking engines?",
        answer: "Yes, this is our core specialty. We have built engineering-grade 2D/3D post-frame building configurators (like DIY Agriculture Buildings), real-time Web Audio API spectrum visualizers (Saved By The Scars), multi-unit apartment vacancy locators (Meritage Management), and instant square-footage painting quote engines (Thumb Premier Painting). We can turn your exact estimating math or service workflow into an intuitive visual customer experience."
      },
      {
        question: "How does built-in Local SEO work on your websites?",
        answer: "Instead of treating SEO as an afterthought or a separate monthly plugin, we bake search engine architecture directly into your site structure. We construct dedicated county and municipal service silos, optimize technical schema markup, configure fast server-side rendering, and connect your Google Business Profile so you rank for high-intent 'near me' commercial searches across your entire operating territory."
      },
      {
        question: "What is speed-to-lead and how is it integrated into the website?",
        answer: "Speed-to-lead means contacting an inbound prospect while their intent is at its peak. Research shows contacting a lead within 60 seconds increases conversion rates by up to 391%. Every Valentine Digital website connects webhooks and forms directly to instant SMS notifications, voice dispatch, and your CRM pipeline so your team can engage prospects before they click the next Google search result."
      },
      {
        question: "How long does a custom platform build take from start to launch?",
        answer: "A standard high-performance service website typically launches in 2 to 4 weeks. Custom web applications or complex platforms with interactive configurators, customer portals, or payment architectures usually take 4 to 8 weeks. We provide transparent weekly sprint demos so you can test working software every step of the way."
      }
    ],
    process: [
      { step: "Architectural Scope & Strategy", description: "We analyze your exact customer journey, profit margins, service territory, and competitors to design a high-converting platform blueprint." },
      { step: "Custom UX/UI & Interactive Prototyping", description: "We craft custom visual designs and interactive tool flows (quote calculators, selectors, booking funnels) tailored to your brand identity." },
      { step: "Full-Stack Development & Local SEO Silos", description: "We engineer your site with ultra-clean modern code, lightning-fast load times, and structured city/county landing pages built right in." },
      { step: "Speed-to-Lead & CRM Integration", description: "We connect instant SMS lead notifications, automated email confirmations, and your CRM pipeline so no lead ever slips through the cracks." },
      { step: "Launch, Domain Hardening & Hand-off", description: "We deploy your site to enterprise global edge networks, run Core Web Vitals checks, test across 10+ mobile devices, and hand over full ownership." }
    ],
    relatedServices: ["seo", "app-development", "ecommerce", "gmb-optimization"],
    tier: 1,
    category: "creative",
    isPrimary: true
  },
  // === TIER 1: LOCAL / GMB ===
  {
    id: "gmb-optimization",
    title: "Google Business Profile Management",
    tagline: "Dominate Local Search Results",
    description: "Your Google Business Profile is often the first impression customers get. We optimize and manage it to drive more calls, visits, and reviews.",
    benefits: [
      "Higher rankings in local map pack",
      "More 5-star reviews from happy customers",
      "Optimized posts and updates that engage",
      "Accurate info across all platforms"
    ],
    icon: Search,
    image: gmbOptimizationImg,
    problemSolved: "Is your Google listing buried while competitors show up first?",
    approach: "Complete profile optimization, review management strategy, regular posts, and local SEO signals that boost visibility.",
    resultMetric: undefined,
    faqs: [
      {
        question: "Why is Google Business Profile important?",
        answer: "Your Google Business Profile appears in local searches and Google Maps. It's often the first thing potential customers see, and a well-optimized profile can drive significantly more calls, website visits, and foot traffic."
      },
      {
        question: "How do you get more reviews?",
        answer: "We implement a systematic review generation strategy that makes it easy for happy customers to leave reviews. This includes follow-up sequences, direct links, and training your team on the best times to ask."
      },
      {
        question: "How often should I post on Google Business Profile?",
        answer: "We recommend posting at least once per week to keep your profile active and engaging. Our management includes regular posts about offers, events, and updates that drive engagement."
      },
      {
        question: "Can you remove negative reviews?",
        answer: "While we can't remove legitimate reviews, we can help you respond professionally to negative feedback and implement strategies to generate more positive reviews that improve your overall rating."
      }
    ],
    process: [
      { step: "Profile Audit", description: "We analyze your current profile completeness, accuracy, and optimization opportunities." },
      { step: "Complete Optimization", description: "We optimize every field—categories, services, attributes, photos, and description." },
      { step: "Review Strategy", description: "We implement systems to generate more positive reviews from satisfied customers." },
      { step: "Weekly Posts", description: "We create and publish engaging posts that keep your profile active and visible." },
      { step: "Monthly Reporting", description: "You receive clear reports on views, calls, direction requests, and review growth." }
    ],
    relatedServices: ["local-service-ads", "seo", "analytics"],
    tier: 1,
    category: "seo",
    isPrimary: true
  },
  // === TIER 1: SOCIAL MEDIA ===
  {
    id: "social-media",
    title: "Social Media Marketing",
    tagline: "Build a Community, Not Just a Following",
    description: "Turn social media into a lead generation machine. We create engaging content and run targeted campaigns that grow your business.",
    benefits: [
      "Consistent brand presence across platforms",
      "Engaging content that resonates with your audience",
      "Targeted ads that reach your ideal customers",
      "Community building that drives loyalty"
    ],
    icon: Palette,
    image: socialMediaImg,
    problemSolved: "Struggling to stand out in crowded social feeds?",
    approach: "Content strategy, consistent posting, community engagement, and paid amplification where it counts.",
    resultMetric: undefined,
    faqs: [
      {
        question: "Which social media platforms should my business be on?",
        answer: "It depends on where your customers spend their time. B2B companies often do well on LinkedIn, while B2C businesses typically focus on Facebook and Instagram. We'll recommend the right mix based on your audience and goals."
      },
      {
        question: "How often should we post on social media?",
        answer: "Consistency matters more than frequency. For most businesses, 3-5 posts per week on primary platforms is ideal. Quality content that engages your audience beats high-volume posting every time."
      },
      {
        question: "Can social media generate real leads?",
        answer: "Absolutely. With the right strategy combining organic content and targeted advertising, social media can be a significant lead source. We've helped clients generate hundreds of leads through social campaigns."
      },
      {
        question: "Do I need to be on TikTok?",
        answer: "TikTok can be powerful for certain businesses, especially those targeting younger demographics. We'll assess whether it makes sense for your specific business and goals—not every business needs to be on every platform."
      }
    ],
    process: [
      { step: "Audit & Strategy", description: "We analyze your current presence and develop a strategy aligned with your goals." },
      { step: "Content Planning", description: "We create a content calendar with themes, post types, and campaign ideas." },
      { step: "Content Creation", description: "We produce engaging graphics, copy, and videos for your approved calendar." },
      { step: "Community Management", description: "We engage with your audience, respond to comments, and build relationships." },
      { step: "Paid Amplification", description: "We boost top content and run targeted ads to expand reach and generate leads." }
    ],
    relatedServices: ["social-media-management", "graphic-design", "video-creation"],
    tier: 1,
    category: "advertising",
    isPrimary: true
  },
  // === TIER 2: APP DEVELOPMENT ===
  {
    id: "app-development",
    title: "App & Software Development",
    tagline: "Custom Solutions for Your Business",
    description: "From mobile apps to custom web applications, we build software that solves real business problems and scales with your growth.",
    benefits: [
      "Custom solutions tailored to your needs",
      "Scalable architecture for future growth",
      "Cross-platform mobile app development",
      "Ongoing support and maintenance"
    ],
    icon: Code,
    image: appDevelopmentImg,
    problemSolved: "Need custom software but don't know where to start?",
    approach: "We follow agile development practices, delivering working software in sprints with regular demos and feedback loops.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What technologies do you use?",
        answer: "We specialize in React and React Native for web and mobile apps, with Node.js backends. We also work with modern frameworks like Next.js and use cloud services like AWS, Google Cloud, and Supabase."
      },
      {
        question: "How long does app development take?",
        answer: "Timeline depends on complexity. A simple MVP might take 1-2 weeks, while a full-featured app could take 6-12 months. We'll provide a detailed timeline after understanding your requirements."
      },
      {
        question: "Do you build iOS and Android apps?",
        answer: "Yes, we build cross-platform mobile apps using React Native, which allows us to deploy to both iOS and Android from a single codebase, saving you time and money."
      },
      {
        question: "What about ongoing maintenance?",
        answer: "We offer maintenance packages that include bug fixes, security updates, performance optimization, and new feature development. Most apps require ongoing support to stay current."
      }
    ],
    process: [
      { step: "Discovery", description: "We define requirements, user stories, and technical architecture for your project." },
      { step: "Design", description: "We create UI/UX designs and interactive prototypes for your approval." },
      { step: "Development", description: "We build your app in sprints with regular demos and feedback sessions." },
      { step: "Testing", description: "We conduct thorough QA testing across devices and browsers." },
      { step: "Launch & Support", description: "We deploy your app and provide ongoing maintenance and updates." }
    ],
    relatedServices: ["ui-ux-design", "web-design", "analytics"],
    tier: 2,
    category: "business-solutions"
  },
  // === TIER 3: SOCIAL MEDIA MANAGEMENT ===
  {
    id: "social-media-management",
    title: "Social Media Management",
    tagline: "Your Social Presence, Handled",
    description: "Consistent, engaging social media presence without the daily grind. We manage your accounts so you can focus on running your business.",
    benefits: [
      "Consistent posting across all platforms",
      "Content creation and scheduling",
      "Community engagement and response",
      "Performance reporting and optimization"
    ],
    icon: Share2,
    image: socialMediaManagementImg,
    problemSolved: "No time to keep up with social media demands?",
    approach: "Content calendars, batch creation, scheduled posting, and active community management.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What platforms do you manage?",
        answer: "We manage all major platforms including Facebook, Instagram, LinkedIn, Twitter/X, TikTok, and Pinterest. We'll recommend the right mix based on where your audience is most active."
      },
      {
        question: "Do I need to approve every post?",
        answer: "We can work either way. Some clients prefer to approve each post; others trust us to handle everything. We typically recommend a monthly content calendar approval with the flexibility to post timely content as needed."
      },
      {
        question: "Will you respond to comments and messages?",
        answer: "Yes, community management is included in our management packages. We respond to comments, handle basic inquiries, and escalate important issues to you promptly."
      },
      {
        question: "How do you come up with content ideas?",
        answer: "We create a content strategy based on your business, industry trends, and what resonates with your audience. We also repurpose your existing content and stay on top of relevant topics in your space."
      }
    ],
    process: [
      { step: "Onboarding", description: "We learn your brand, voice, goals, and get access to your accounts." },
      { step: "Content Strategy", description: "We develop a content strategy and themes aligned with your business goals." },
      { step: "Content Calendar", description: "We create and share a monthly calendar for your review and approval." },
      { step: "Publishing & Engagement", description: "We post content and manage community engagement daily." },
      { step: "Reporting", description: "We provide monthly reports on growth, engagement, and key metrics." }
    ],
    relatedServices: ["social-media", "graphic-design", "video-creation"],
    tier: 3,
    category: "advertising"
  },
  // === TIER 3: EMAIL MARKETING ===
  {
    id: "email-marketing",
    title: "Email Marketing",
    tagline: "Nurture Leads Into Loyal Customers",
    description: "Email isn't dead—it's your highest ROI channel. We create automated sequences that convert leads and keep customers coming back.",
    benefits: [
      "Automated follow-up that never forgets",
      "Personalized campaigns that resonate",
      "Re-engage past customers profitably",
      "Measurable revenue from every send"
    ],
    icon: Mail,
    image: emailMarketingImg,
    problemSolved: "Leaving money on the table by not following up with leads?",
    approach: "List building, segmentation, automated sequences, and regular campaigns that drive action.",
    resultMetric: undefined,
    faqs: [
      {
        question: "Is email marketing still effective?",
        answer: "Absolutely. Email consistently delivers the highest ROI of any marketing channel—around $42 for every $1 spent. Unlike social media, you own your email list and aren't subject to algorithm changes."
      },
      {
        question: "How often should I email my list?",
        answer: "It depends on your audience and content. Most businesses do well with 1-4 emails per month. The key is providing value—not just promotional content. We'll help you find the right frequency."
      },
      {
        question: "What email platform do you use?",
        answer: "We work with all major platforms including Mailchimp, Klaviyo, ActiveCampaign, and HubSpot. We'll recommend the best platform based on your needs, list size, and budget."
      },
      {
        question: "Can you help grow my email list?",
        answer: "Yes. We implement lead magnets, opt-in forms, and other list-building strategies to grow your subscriber base with qualified leads who want to hear from you."
      }
    ],
    process: [
      { step: "Strategy & Audit", description: "We review your current email efforts and develop a strategy for growth." },
      { step: "List Segmentation", description: "We segment your list to deliver more relevant, personalized messages." },
      { step: "Automation Setup", description: "We create automated sequences for welcome, nurture, and re-engagement." },
      { step: "Campaign Creation", description: "We design and write regular campaigns that engage and convert." },
      { step: "Testing & Optimization", description: "We A/B test subject lines, content, and timing to improve results." }
    ],
    relatedServices: ["analytics", "web-design", "social-media"],
    tier: 3,
    category: "advertising"
  },
  // === TIER 2: ECOMMERCE ===
  {
    id: "ecommerce",
    title: "Ecommerce Solutions",
    tagline: "Sell More, Stress Less",
    description: "Launch or optimize your online store with conversion-focused design, seamless checkout experiences, and integrated marketing tools.",
    benefits: [
      "High-converting product pages",
      "Streamlined checkout to reduce cart abandonment",
      "Integrated payment and shipping solutions",
      "Inventory and order management"
    ],
    icon: ShoppingCart,
    image: ecommerceImg,
    problemSolved: "Is your online store leaving money on the table?",
    approach: "We build ecommerce experiences optimized for conversions, from product discovery to post-purchase follow-up.",
    resultMetric: undefined,
    faqs: [
      {
        question: "Which ecommerce platforms do you work with?",
        answer: "We work with Shopify, WooCommerce, and custom solutions. We'll recommend the best platform based on your product catalog, sales volume, and specific business requirements."
      },
      {
        question: "Can you migrate my existing store?",
        answer: "Yes, we handle full store migrations including products, customer data, order history, and SEO preservation. We ensure minimal downtime and no loss of search rankings."
      },
      {
        question: "How do you reduce cart abandonment?",
        answer: "We implement proven strategies including simplified checkout, guest checkout options, multiple payment methods, trust signals, exit-intent recovery, and abandoned cart email sequences."
      },
      {
        question: "Do you handle payment gateway setup?",
        answer: "Yes, we set up and configure payment gateways including Stripe, PayPal, Square, and others. We ensure PCI compliance and a smooth checkout experience for your customers."
      }
    ],
    process: [
      { step: "Strategy", description: "We analyze your products, customers, and competition to develop your ecommerce strategy." },
      { step: "Platform Setup", description: "We set up and configure your ecommerce platform with all necessary integrations." },
      { step: "Design & Development", description: "We design and build your store with conversion optimization in mind." },
      { step: "Product Setup", description: "We help migrate or create product listings with optimized descriptions and images." },
      { step: "Launch & Optimize", description: "We launch your store and continuously optimize based on performance data." }
    ],
    relatedServices: ["web-design", "ppc", "email-marketing"],
    tier: 2,
    category: "business-solutions"
  },
  // === TIER 3: ANALYTICS ===
  {
    id: "analytics",
    title: "Analytics & Reporting",
    tagline: "Make Smarter Decisions With Real Data",
    description: "Stop guessing and start knowing. We set up tracking, analyze your data, and give you actionable insights to grow your business.",
    benefits: [
      "Clear dashboards showing what's working",
      "Identify your most profitable channels",
      "Track ROI on every marketing effort",
      "Data-driven recommendations for growth"
    ],
    icon: BarChart3,
    image: analyticsImg,
    problemSolved: "Flying blind without knowing what's actually working?",
    approach: "Proper tracking setup, custom dashboards, monthly analysis, and strategic recommendations.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What analytics tools do you use?",
        answer: "We primarily use Google Analytics 4, Google Tag Manager, and Google Looker Studio for dashboards. We can also integrate with platform-specific analytics like Meta, LinkedIn, and your CRM."
      },
      {
        question: "What should I be tracking?",
        answer: "At minimum, you should track website traffic, lead form submissions, phone calls, and revenue by channel. We'll help you identify the key metrics that matter most for your specific business."
      },
      {
        question: "How often will I receive reports?",
        answer: "We provide monthly reports with key metrics, trends, and insights. You'll also have access to real-time dashboards so you can check performance anytime you want."
      },
      {
        question: "Can you help with attribution?",
        answer: "Yes, we set up proper attribution tracking so you can see which marketing channels are driving results. This helps you invest more in what's working and cut what's not."
      }
    ],
    process: [
      { step: "Audit", description: "We review your current tracking setup and identify gaps and opportunities." },
      { step: "Implementation", description: "We set up proper tracking for all your marketing channels and conversion points." },
      { step: "Dashboard Creation", description: "We build custom dashboards showing the metrics that matter to your business." },
      { step: "Training", description: "We train you on how to read and use your dashboards to make decisions." },
      { step: "Ongoing Analysis", description: "We provide monthly analysis with insights and recommendations for improvement." }
    ],
    relatedServices: ["ppc", "seo", "email-marketing"],
    tier: 3,
    category: "advertising"
  },
  // === TIER 2: SALES FUNNELS ===
  {
    id: "sales-funnels",
    title: "Sales Funnels",
    tagline: "Convert Visitors Into Customers, Automatically",
    description: "Strategic sales funnels that guide prospects through their buying journey, nurture leads, and maximize customer lifetime value.",
    benefits: [
      "High-converting landing pages",
      "Automated email nurture sequences",
      "Upsell and cross-sell optimization",
      "Complete funnel analytics and tracking"
    ],
    icon: Target,
    image: salesFunnelsImg,
    problemSolved: "Getting traffic but not converting visitors into customers?",
    approach: "We build complete sales systems with landing pages, lead magnets, email sequences, and conversion optimization.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What is a sales funnel?",
        answer: "A sales funnel is the journey customers take from first discovering your brand to making a purchase. It typically includes landing pages, lead capture, email nurturing, and sales pages designed to guide prospects toward conversion."
      },
      {
        question: "What platforms do you use for funnels?",
        answer: "We can build funnels using various platforms including ClickFunnels, custom-coded solutions, WordPress, and integrated with your existing website. We'll recommend the best approach based on your needs."
      },
      {
        question: "How long does it take to build a funnel?",
        answer: "A basic funnel can be built in 2-3 weeks. A comprehensive funnel with multiple entry points, email sequences, and optimization typically takes 4-8 weeks to fully develop and test."
      },
      {
        question: "Do you write the copy for funnel pages?",
        answer: "Yes, our funnel service includes conversion-focused copywriting for all pages and email sequences. Great copy is essential to funnel success, so it's included in our packages."
      }
    ],
    process: [
      { step: "Strategy", description: "We map your customer journey and identify the optimal funnel structure." },
      { step: "Content & Copy", description: "We create compelling copy and content for each stage of the funnel." },
      { step: "Build", description: "We design and develop landing pages, forms, and email sequences." },
      { step: "Integration", description: "We connect all tools—CRM, email, payment, and analytics." },
      { step: "Optimize", description: "We test, analyze, and continuously improve funnel performance." }
    ],
    relatedServices: ["ppc", "email-marketing", "web-design"],
    tier: 2,
    category: "business-solutions"
  },
  // === TIER 3: UI/UX DESIGN ===
  {
    id: "ui-ux-design",
    title: "UI/UX Design",
    tagline: "Designs That Users Love to Use",
    description: "Create intuitive, beautiful digital experiences that delight users and drive conversions. We combine user research with stunning visual design.",
    benefits: [
      "User-centered design that reduces friction",
      "Intuitive interfaces that users love",
      "Higher conversion rates through better UX",
      "Consistent design systems for scalability"
    ],
    icon: Smartphone,
    image: uiUxDesignImg,
    problemSolved: "Is your digital product confusing users and losing customers?",
    approach: "We conduct user research, create wireframes and prototypes, and iterate based on real user feedback to deliver exceptional experiences.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What's the difference between UI and UX design?",
        answer: "UX (User Experience) focuses on how a product feels and functions—the overall journey a user takes. UI (User Interface) focuses on how it looks—colors, typography, buttons, and visual elements. We provide both to ensure your product is both beautiful and functional."
      },
      {
        question: "Do you conduct user research?",
        answer: "Yes, user research is fundamental to our process. We conduct user interviews, surveys, usability testing, and analyze user behavior data to inform our design decisions."
      },
      {
        question: "What deliverables will I receive?",
        answer: "Depending on your project, you'll receive user personas, user journey maps, wireframes, high-fidelity mockups, interactive prototypes, and a complete design system with components and style guides."
      },
      {
        question: "How do you handle design iterations?",
        answer: "We work in iterative cycles, presenting designs at key milestones for your feedback. Our packages typically include 2-3 revision rounds per phase to ensure we get the design exactly right."
      }
    ],
    process: [
      { step: "Research", description: "We study your users, competitors, and business goals to inform the design strategy." },
      { step: "Wireframing", description: "We create low-fidelity layouts to establish information architecture and user flows." },
      { step: "Visual Design", description: "We develop high-fidelity mockups with your brand's visual identity applied." },
      { step: "Prototyping", description: "We build interactive prototypes for testing and stakeholder review." },
      { step: "Handoff", description: "We deliver design files, specifications, and assets ready for development." }
    ],
    relatedServices: ["web-design", "app-development", "branding"],
    tier: 3,
    category: "creative"
  },
  // === TIER 3: GRAPHIC DESIGN ===
  {
    id: "graphic-design",
    title: "Graphic Design",
    tagline: "Visuals That Capture Attention",
    description: "From social media graphics to print materials, we create stunning visuals that communicate your brand message and drive engagement.",
    benefits: [
      "Consistent brand visuals across all channels",
      "Eye-catching social media graphics",
      "Professional print and digital materials",
      "Fast turnaround on design requests"
    ],
    icon: PenTool,
    image: graphicDesignImg,
    problemSolved: "Struggling with inconsistent or amateur-looking graphics?",
    approach: "We learn your brand, create templated systems for efficiency, and deliver polished designs that elevate your presence.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What types of graphics do you create?",
        answer: "We create social media graphics, email headers, digital ads, infographics, presentations, print materials (brochures, flyers, business cards), and more. If it's visual, we can design it."
      },
      {
        question: "How fast can you turn around designs?",
        answer: "Standard turnaround is 2-3 business days for most projects. We also offer rush services for urgent needs. With a retainer, you get priority scheduling and faster turnaround."
      },
      {
        question: "Will I own the design files?",
        answer: "Yes. Once your project is complete and paid for, you own all the design files. We provide them in the formats you need for both print and digital use."
      },
      {
        question: "Do you offer ongoing design support?",
        answer: "Yes, we offer monthly design retainers that give you a set number of design hours each month. This is ideal for businesses that need consistent design support."
      }
    ],
    process: [
      { step: "Brief", description: "We understand your needs, goals, and brand guidelines for the project." },
      { step: "Concept Development", description: "We create initial concepts based on your brief and requirements." },
      { step: "Revision", description: "You provide feedback and we refine the design until you're happy." },
      { step: "Finalization", description: "We finalize the design and prepare files in all needed formats." },
      { step: "Delivery", description: "You receive all files along with usage guidelines if needed." }
    ],
    relatedServices: ["branding", "social-media", "web-design"],
    tier: 3,
    category: "creative"
  },
  // === TIER 3: BRANDING ===
  {
    id: "branding",
    title: "Brand Identity & Strategy",
    tagline: "Build a Brand That Stands Out",
    description: "Your brand is more than a logo—it's how customers perceive you. We craft complete brand identities that resonate with your target audience.",
    benefits: [
      "Logo design that captures your essence",
      "Complete brand guidelines for consistency",
      "Messaging that connects emotionally",
      "Competitive positioning that differentiates"
    ],
    icon: Sparkles,
    image: brandingImg,
    problemSolved: "Does your brand blend in instead of standing out?",
    approach: "Discovery, competitive analysis, visual identity development, and comprehensive brand guidelines.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What's included in a brand identity package?",
        answer: "Our complete package includes logo design (primary, secondary, and icon versions), color palette, typography, brand guidelines document, and brand messaging framework. We can also add stationery, social templates, and more."
      },
      {
        question: "How long does a branding project take?",
        answer: "A full brand identity project typically takes 4-6 weeks. This includes discovery, concept development, revisions, and finalization of all deliverables."
      },
      {
        question: "What if I just need a logo?",
        answer: "We offer logo-only packages for businesses that don't need a full brand identity. However, we recommend at least a mini brand guide to ensure consistent usage."
      },
      {
        question: "How many logo concepts will I see?",
        answer: "We typically present 3 distinct logo concepts. From there, you choose your favorite direction and we refine it through 2-3 rounds of revisions until it's perfect."
      }
    ],
    process: [
      { step: "Discovery", description: "We dive deep into your business, values, audience, and competitive landscape." },
      { step: "Strategy", description: "We develop your brand positioning, personality, and messaging framework." },
      { step: "Visual Identity", description: "We design your logo, color palette, typography, and visual elements." },
      { step: "Guidelines", description: "We create comprehensive brand guidelines for consistent application." },
      { step: "Rollout Support", description: "We help you apply your new brand across key touchpoints." }
    ],
    relatedServices: ["web-design", "graphic-design", "video-creation"],
    tier: 3,
    category: "creative"
  },
  // === TIER 3: VIDEO CREATION ===
  {
    id: "video-creation",
    title: "Video Creation & Editing",
    tagline: "Tell Your Story Through Video",
    description: "Video is the most engaging content format. We create and edit compelling videos for social media, ads, and your website.",
    benefits: [
      "Professional video content for all platforms",
      "Social media clips optimized for engagement",
      "Ad creative that converts",
      "Brand storytelling that resonates"
    ],
    icon: Video,
    image: videoCreationImg,
    problemSolved: "Missing out on video's massive engagement potential?",
    approach: "Concept development, filming or stock sourcing, professional editing, and platform-optimized delivery.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What types of videos do you create?",
        answer: "We create social media videos, promotional videos, testimonial videos, explainer videos, product demos, and video ads. We handle everything from concept to final delivery."
      },
      {
        question: "Do you provide filming services?",
        answer: "Yes, we offer on-location filming for testimonials, behind-the-scenes, and promotional content. We can also work with footage you provide or source high-quality stock footage."
      },
      {
        question: "What's the typical video length?",
        answer: "It depends on the platform and purpose. Social media videos are typically 15-60 seconds. Explainer videos are 1-2 minutes. We'll recommend the right length based on your goals."
      },
      {
        question: "How many revisions do I get?",
        answer: "Our packages include 2 rounds of revisions on all video projects. We share a detailed brief before starting to ensure we're aligned, minimizing the need for extensive changes."
      }
    ],
    process: [
      { step: "Concept & Brief", description: "We develop the video concept, script, and shot list together." },
      { step: "Production", description: "We film on location or source/compile the footage needed." },
      { step: "Editing", description: "We edit the video with graphics, music, and effects as needed." },
      { step: "Review", description: "You review the draft and we make revisions based on your feedback." },
      { step: "Delivery", description: "We deliver optimized versions for each platform you need." }
    ],
    relatedServices: ["social-media", "ppc", "branding"],
    tier: 3,
    category: "creative"
  },
  // === TIER 3: BUSINESS CONSULTING ===
  {
    id: "business-consulting",
    title: "Business Consulting",
    tagline: "Build Systems That Scale Your Business",
    description: "Strategic consulting for businesses that need internal systems, tool implementation, and hands-on training to operate more efficiently.",
    benefits: [
      "Custom CRM and internal tool implementation",
      "Hands-on staff training and documentation",
      "Process optimization and workflow automation",
      "Strategic advisory for sustainable growth"
    ],
    icon: Briefcase,
    image: businessConsultingImg,
    problemSolved: "Overwhelmed by processes, tools, and systems that don't work together?",
    approach: "We assess your current operations, identify bottlenecks, implement the right tools, and train your team to use them effectively.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What types of businesses do you consult for?",
        answer: "We work with small to mid-sized businesses across industries including automotive dealerships, service companies, retail, and professional services. If you have operational challenges or need better systems, we can help."
      },
      {
        question: "Do you provide ongoing support after implementation?",
        answer: "Yes, we offer ongoing advisory retainers for businesses that want continued guidance. This includes monthly check-ins, system optimization, and on-demand support for new challenges."
      },
      {
        question: "What tools and systems do you help implement?",
        answer: "We implement CRMs, project management tools, reporting dashboards, automated workflows, and custom internal applications. We also provide white-labeled SEO and reporting tools for agencies and consultants."
      },
      {
        question: "How long does a typical consulting engagement take?",
        answer: "Initial assessments take 1-2 weeks. Full implementations typically run 4-8 weeks depending on complexity. Many clients continue with monthly advisory support afterward."
      }
    ],
    process: [
      { step: "Discovery & Audit", description: "We analyze your current operations, tools, and pain points to understand what's working and what isn't." },
      { step: "Strategy & Roadmap", description: "We create a prioritized roadmap of improvements with clear timelines and expected outcomes." },
      { step: "Tool Implementation", description: "We set up and configure the right tools for your business, integrating them with existing systems." },
      { step: "Team Training", description: "We train your staff with hands-on sessions and create documentation they can reference." },
      { step: "Ongoing Support", description: "We provide continued advisory and support to ensure long-term success and adoption." }
    ],
    relatedServices: ["analytics", "app-development", "web-design"],
    tier: 3,
    category: "business-solutions"
  },
  // === TIER 3: PRODUCT MARKETING ===
  {
    id: "product-marketing",
    title: "Product Photography & Marketing",
    tagline: "Make Your Products Irresistible",
    description: "Professional product photography and marketing that showcases your products in their best light and drives purchase decisions.",
    benefits: [
      "Studio-quality product photography",
      "Lifestyle shots that tell your brand story",
      "Amazon and marketplace-optimized images",
      "360-degree product views and videos"
    ],
    icon: Camera,
    image: productMarketingImg,
    problemSolved: "Are poor product images killing your conversions?",
    approach: "We combine professional photography with strategic marketing to create visuals that sell.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What types of product photography do you offer?",
        answer: "We offer white background studio shots, lifestyle photography, flat lays, 360-degree spins, detail shots, and packaging photography. We'll recommend the right mix based on your products and platforms."
      },
      {
        question: "Do you shoot on location?",
        answer: "Yes, we can shoot at your location, our studio, or on location for lifestyle shots. We'll discuss the best approach based on your products and the type of imagery you need."
      },
      {
        question: "How many images will I receive?",
        answer: "This depends on your package. Typically you'll receive 5-10 edited images per product for ecommerce, plus additional lifestyle shots. We'll provide both web-optimized and high-resolution versions."
      },
      {
        question: "Can you create images for Amazon?",
        answer: "Absolutely. We're familiar with Amazon's image requirements and create compliant main images plus enhanced brand content images that help your products stand out in search results."
      }
    ],
    process: [
      { step: "Planning", description: "We discuss your products, brand aesthetic, and image requirements." },
      { step: "Styling", description: "We prepare props, backgrounds, and styling elements for the shoot." },
      { step: "Photography", description: "We capture your products with professional lighting and equipment." },
      { step: "Editing", description: "We retouch and optimize images for your sales channels." },
      { step: "Delivery", description: "You receive all images in multiple formats ready for use." }
    ],
    relatedServices: ["ecommerce", "graphic-design", "branding"],
    tier: 3,
    category: "creative"
  },
  // === TIER 2: CUSTOM BUSINESS SOLUTIONS ===
  {
    id: "custom-business-solutions",
    title: "Custom Business Solutions",
    tagline: "Your Business, Your Platform, Built Exactly How You Need It",
    description: "We design and build complete business platforms—from custom CRMs and client portals to industry-specific SaaS tools. No more duct-taping tools together. Get a unified system that does exactly what YOUR business needs.",
    benefits: [
      "Custom CRM tailored to your workflow",
      "Client portals your customers will love",
      "Backend integrations that connect everything",
      "Niche-specific features built for your industry",
      "Scalable architecture that grows with you"
    ],
    icon: Layers,
    image: customBusinessSolutionsImg,
    problemSolved: "Tired of paying for 10 different tools that don't talk to each other?",
    approach: "We analyze your business processes, design a unified system, and build it in sprints with your feedback at every step.",
    resultMetric: undefined,
    faqs: [
      {
        question: "What kind of platforms do you build?",
        answer: "We build custom CRMs, client portals, booking and scheduling systems, inventory management, custom dashboards, industry-specific tools, and complete business management platforms. If you can dream it, we can build it."
      },
      {
        question: "How long does it take to build a custom platform?",
        answer: "Timeline depends on complexity. A focused MVP typically takes 1-2 weeks. Full-featured platforms with multiple integrations can take 3-6 months. We work in sprints so you see progress every 2 weeks."
      },
      {
        question: "Is it really 100% custom?",
        answer: "Yes. We don't use templates or off-the-shelf solutions that we just customize. Every platform is architected and built specifically for your business needs, workflows, and goals."
      },
      {
        question: "Can you integrate with my existing tools?",
        answer: "Absolutely. We specialize in connecting to existing systems—accounting software, payment processors, email platforms, and more. Your new platform becomes the hub that ties everything together."
      },
      {
        question: "What about ongoing support and updates?",
        answer: "We offer maintenance packages that include bug fixes, security updates, performance optimization, and new feature development. Your platform evolves as your business grows."
      }
    ],
    process: [
      { step: "Discovery", description: "We dive deep into your business, workflows, pain points, and goals to understand exactly what you need." },
      { step: "Design", description: "We map out your ideal system with wireframes, prototypes, and user flows for your approval." },
      { step: "Build", description: "We develop your platform in 2-week sprints with regular demos and feedback sessions." },
      { step: "Launch", description: "We deploy your platform, migrate data, and train your team on how to use it." },
      { step: "Support", description: "We provide ongoing maintenance, support, and feature development as your business evolves." }
    ],
    relatedServices: ["app-development", "web-design", "ecommerce", "business-consulting"],
    tier: 2,
    category: "business-solutions"
  }
];

// Helper functions for filtering services by tier
export const getTier1Services = () => services.filter(s => s.tier === 1);
export const getTier2Services = () => services.filter(s => s.tier === 2);
export const getTier3Services = () => services.filter(s => s.tier === 3);
export const getPrimaryServices = () => services.filter(s => s.isPrimary);
export const getServicesByCategory = (category: ServiceCategory) => services.filter(s => s.category === category);
